import React from 'react'
import { Button, Card, Modal, Form, Input, Typography, message, Space, Select, Divider, Table, Tag } from 'antd'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { listOrders, createOrder, updateOrder, addSubOrderItem, updateSubOrderItem, deleteSubOrderItem } from '../../api/orders'
import { listDeliveryLocations } from '../../api/deliveryLocations'
import { useNavigate } from 'react-router-dom'

export default function AdminOrders() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [showCreate, setShowCreate] = React.useState(false)
  const [form] = Form.useForm()

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['adminOrders'],
    queryFn: listOrders,
  })


  const { data: locations } = useQuery({
    queryKey: ['deliveryLocationsAll'],
    queryFn: listDeliveryLocations,
  })
  const locMap = React.useMemo(() => {
    const m = new Map()
    ;(Array.isArray(locations) ? locations : []).forEach(l => m.set(l.id, l))
    return m
  }, [locations])

  async function submitCreate(values) {
    try {
      const payload = {
        customerName: values.name,
        customerMobile: values.mobile,
        deliveryLocationId: Number(values.deliveryLocationId),
        deliveryDetails: values.deliveryDetails || null,
        status: 'new',
        subOrders: [],
      }
      const created = await createOrder(payload)
      message.success('تم إنشاء الطلب')
      setShowCreate(false)
      form.resetFields()
      qc.invalidateQueries({ queryKey: ['adminOrders'] })
      if (created?.id) {
        navigate(`/admin/orders/${created.id}`)
      }
    } catch (e) {
      console.error(e)
      message.error('فشل إنشاء الطلب')
    }
  }

  return (
    <div style={{ maxWidth: 1100, margin: '40px auto', padding: '0 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>الطلبات</Typography.Title>
        <Button type="primary" onClick={() => setShowCreate(true)}>طلب جديد</Button>
      </div>

      <Card>
        {isLoading && <div>Loading...</div>}
        {isError && (
          <div>
            <Typography.Text type="danger">Error: {String(error?.message || 'Failed')}</Typography.Text>
            <div><Button onClick={() => refetch()}>Retry</Button></div>
          </div>
        )}
        {!isLoading && !isError && (
          <Table
            rowKey="id"
            columns={[
              { title: 'الإسم', key: 'userName', render: (_, r) => r.user?.name || '—' },
              { title: 'الجوال', key: 'userPhone', width: 140, render: (_, r) => r.user?.phone || '—' },
              { title: 'العنوان التفصيلي', key: 'userFullAddress', render: (_, r) => r.user?.fullAddress || '—' },
              { title: 'منطقة التوصيل', key: 'deliveryLocation', width: 200, render: (_, r) => r.user?.deliveryLocation?.name || '—' },
              { title: 'ملاحظات', dataIndex: 'notes', key: 'notes', width: 240 },
              { title: 'الإجمالي', dataIndex: 'totalAfterDiscount', key: 'totalAfterDiscount', width: 120 },
              { title: 'الحالة', dataIndex: 'status', key: 'status', width: 180, render: (v) => <Tag color={v === 'delivered' ? 'green' : v === 'cancelled' ? 'red' : 'blue'}>{{'new':'جديد','confirmed with shops':'تم التأكيد مع المتاجر','notified driver':'تم إشعار السائق','delivered':'تم التوصيل','cancelled':'أُلغي'}[v] || v}</Tag> },
            ]}
            dataSource={Array.isArray(data) ? data : []}
            pagination={{ pageSize: 10 }}
            onRow={(record) => ({ onClick: () => navigate(`/admin/orders/${record.id}`) })}
          />
        )}
      </Card>

      <Modal
        open={showCreate}
        onCancel={() => setShowCreate(false)}
        onOk={() => form.submit()}
        okText="إنشاء"
        title="إنشاء طلب"
      >
        <Form form={form} layout="vertical" onFinish={submitCreate}>
          <Form.Item label="اسم الزبون" name="name" rules={[{ required: true, message: 'يرجى إدخال الاسم' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="جوال الزبون" name="mobile" rules={[{ required: true, message: 'يرجى إدخال الجوال' }]} >
            <Input />
          </Form.Item>
          <Form.Item label="منطقة التوصيل" name="deliveryLocationId" rules={[{ required: true, message: 'يرجى اختيار منطقة' }]} >
            <Select
              showSearch
              optionFilterProp="label"
              options={(locations || []).map(l => ({ value: l.id, label: l.nameAr || l.name || `#${l.id}` }))}
            />
          </Form.Item>
          <Form.Item label="تفاصيل التوصيل" name="deliveryDetails">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}


function StatusSelect({ order, onChanged }) {
  const [loading, setLoading] = React.useState(false)
  const [value, setValue] = React.useState(order.status)

  async function save() {
    try {
      setLoading(true)
      await updateOrder(order.id, { status: value })
      message.success('Status updated')
      onChanged?.()
    } catch (e) {
      console.error(e)
      message.error('Failed to update status')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <select value={value} onChange={e => setValue(e.target.value)} disabled={loading}>
        {['new','confirmed with shops','notified driver','delivered','cancelled'].map(s => (
          <option key={s} value={s}>{{'new':'جديد','confirmed with shops':'تم التأكيد مع المتاجر','notified driver':'تم إشعار السائق','delivered':'تم التوصيل','cancelled':'أُلغي'}[s] || s}</option>
        ))}
      </select>
      <Button size="small" onClick={save} loading={loading}>Save</Button>
    </div>
  )
}

function ManageSubOrders({ order, onChanged }) {

  const [showItemModal, setShowItemModal] = React.useState(false)
  const [editingItem, setEditingItem] = React.useState(null) // { subId, item } or { subId }
  const [itemForm] = Form.useForm()


  function openEditItem(sub, it) {
    setEditingItem({ subId: sub.id, item: it })
    itemForm.setFieldsValue({ mapId: it.mapId, quantity: it.quantity })
    setShowItemModal(true)
  }
  async function submitItem(values) {
    try {
      const subId = editingItem.subId
      if (editingItem.item?.id) {
        await updateSubOrderItem(order.id, subId, editingItem.item.id, { mapId: values.mapId, quantity: Number(values.quantity) })
        message.success('تم تحديث العنصر')
      } else {
        await addSubOrderItem(order.id, subId, { mapId: values.mapId, quantity: Number(values.quantity) })
        message.success('تمت إضافة العنصر')
      }
      setShowItemModal(false)
      setEditingItem(null)
      itemForm.resetFields()
      onChanged?.()
    } catch (e) {
      console.error(e)
      message.error('تعذر حفظ العنصر')
    }
  }
  async function removeItem(sub, it) {
    Modal.confirm({
      title: 'حذف العنصر؟',
      onOk: async () => {
        try {
          await deleteSubOrderItem(order.id, sub.id, it.id)
          message.success('تم حذف العنصر')
          onChanged?.()
        } catch (e) {
          console.error(e)
          message.error('فشل حذف العنصر')
        }
      }
    })
  }

  return (
    <div style={{ background: '#fafafa', padding: 8, borderRadius: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Text strong>الطلبات الفرعية</Typography.Text>
      </div>
      <Divider style={{ margin: '8px 0' }} />
      {(order.subOrders || []).length === 0 && (
        <Typography.Text type="secondary">لا توجد طلبات فرعية</Typography.Text>
      )}
      {(order.subOrders || []).map(sub => (
        <Card key={sub.id} size="small" style={{ marginBottom: 8 }} title={<span>متجر: {sub.shopNameAr} (#{sub.shopId}){sub.pickupHour ? ` | ${sub.pickupHour}` : ''} | المجموع: {sub.totalAfterDiscount}</span>} >
          <div style={{ display: 'grid', gap: 6 }}>
            {(sub.items || []).map(it => (
              <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{it.itemNameAr}</strong> — [{it.quantity}×] @ {it.unitPrice} = {it.rowTotal}
                  <span style={{ marginInlineStart: 8, color: '#999' }}>map:{it.mapId}</span>
                </div>
                <Space>
                  <Button size="small" onClick={() => openEditItem(sub, it)}>تعديل</Button>
                  <Button size="small" danger onClick={() => removeItem(sub, it)}>حذف</Button>
                </Space>
              </div>
            ))}
          </div>
        </Card>
      ))}


      {/* Item Modal */}
      <Modal
        open={showItemModal}
        onCancel={() => { setShowItemModal(false); setEditingItem(null) }}
        onOk={() => itemForm.submit()}
        okText={editingItem?.item?.id ? 'Save' : 'Add'}
        title={editingItem?.item?.id ? 'Edit Item' : 'Add Item'}
      >
        <Form form={itemForm} layout="vertical" onFinish={submitItem}>
          <Form.Item label="mapId (ShopItemMap)" name="mapId" rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>
          <Form.Item label="Quantity" name="quantity" rules={[{ required: true, min: 1, type: 'number', transform: v => Number(v) }]}>
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
